import { useEffect } from 'react';
import { Layout } from '../components/Layout';
import { MyEventCard } from '../components/MyEventCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

import { Users, AlertCircle, Loader2, Calendar, Clock } from 'lucide-react';
import { MyEventsService, MyEvent } from '@/api/myEventsService';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useRefreshContext } from '@/contexts/RefreshContext';

export const PastEventsPage = () => {
  const { t } = useTranslation('common');
  const { registerRefresh, unregisterRefresh } = useRefreshContext();

  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['pastEvents'],
    queryFn: MyEventsService.getPastEvents,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Register refresh function that invalidates cache and refetches
  useEffect(() => {
    registerRefresh('/my-events', async () => {
      await queryClient.invalidateQueries({ queryKey: ['pastEvents'] });
    });
    return () => unregisterRefresh('/my-events');
  }, [registerRefresh, unregisterRefresh, queryClient]);

  const { mutate: checkOut, isPending: isCheckingOut } = useMutation({
    mutationFn: ({ eventId, attendeeId }: { eventId: string; attendeeId: string }) =>
      MyEventsService.checkOut(eventId, attendeeId),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['pastEvents'] });
      toast.success(t('pastEvents.checkOutSuccess'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('pastEvents.checkOutError'));
    },
  });

  const handleCheckOut = (eventId: string, attendeeId: string) => {
    checkOut({ eventId, attendeeId });
  };

  const allEvents = data?.events || [];

  const ongoingEvents: MyEvent[] = [];
  const pastEvents: MyEvent[] = [];

  allEvents.forEach((event) => {
    const hasAnyOngoing = event.attendees.some((a) => a.registrationStatus === 'attended' && !a.checkedOutAt);

    if (hasAnyOngoing) {
      ongoingEvents.push(event);
    } else {
      pastEvents.push(event);
    }
  });

  const hasOngoing = ongoingEvents.length > 0;
  const hasPast = pastEvents.length > 0;

  return (
    <Layout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div>
              <CardTitle className="flex items-center gap-2 text-[var(--color-neutral-900)]">
                <Calendar className="h-6 w-6 text-[var(--color-primary-700)]" />
                {t('pastEvents.title')}
              </CardTitle>
              <CardDescription className="text-[var(--color-neutral-700)]">
                {t('pastEvents.description')}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>

        {error && (
          <Card className="border-[var(--color-warning-500)]/30 bg-[var(--color-warning-100)]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-[var(--color-warning-700)]">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm">{(error as Error)?.message || 'Failed to load events'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-8">
          {/* Ongoing Events Section */}
          {hasOngoing && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold text-[var(--color-neutral-900)]">
                  {t('pastEvents.ongoingEvents')}
                </h2>
              </div>

              <div className="space-y-4">
                {ongoingEvents.map((event) => (
                  <MyEventCard
                    key={`ongoing-${event.eventId}`}
                    event={event}
                    variant="ongoing"
                    onCheckOut={handleCheckOut}
                    isCheckingOut={isCheckingOut}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Events Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5" />
              <h2 className="text-xl font-semibold text-[var(--color-neutral-900)]">
                {t('pastEvents.eventAttendance')} ({loading ? '...' : pastEvents.length})
              </h2>
            </div>

            {loading ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Loader2 className="mx-auto h-12 w-12 text-[var(--color-neutral-400)] mb-4 animate-spin" />
                  <h3 className="text-lg font-medium mb-2 text-[var(--color-neutral-900)]">
                    {t('pastEvents.loading')}
                  </h3>
                  <p className="text-sm text-[var(--color-neutral-700)]">{t('pastEvents.loadingDescription')}</p>
                </CardContent>
              </Card>
            ) : hasPast ? (
              <div className="space-y-4">
                {pastEvents.map((event) => (
                  <MyEventCard
                    key={event.eventId}
                    event={event}
                    variant="past"
                    onCheckOut={handleCheckOut}
                    isCheckingOut={isCheckingOut}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Calendar className="mx-auto h-12 w-12 text-[var(--color-neutral-400)] mb-4" />
                  <h3 className="text-lg font-medium mb-2 text-[var(--color-neutral-900)]">
                    {t('pastEvents.noEvents')}
                  </h3>
                  <p className="text-sm text-[var(--color-neutral-700)] mb-6">{t('pastEvents.noEventsDescription')}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
